import React, { useEffect, useState, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import DOMPurify from "dompurify";
import {
  Card,
  CardHeader,
  CardBody,
  Container,
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import AdminHeader from "components/Headers/AdminHeader";
import {
  urlDecodedId,
  urlEncodedId,
} from "components/CommonComponents/encodeDecodeFunction";
import {
  MULTI_CORRECT_NEGATIVE_OPTIONS,
  LANGUAGE_OPTIONS,
  ANSWER_TYPES,
} from "components/constant";
import { NotificationContext } from "components/CommonComponents/NotificationProvider";
import { getQuestionBankById, getQuestionBankDifficultyList } from "action/questionBank";
import DetailsHeader from "components/CommonComponents/DetailsHeader";
import { PREVIEW_LIMIT } from "constants/CommonConstants";
import { toTitleCase } from "components/CommonComponents/CommonFunctions";



function QuestionBankDetails() {

  const { id } = useParams();
  const decodedId = id ? urlDecodedId(id) : null;
  const { notify } = useContext(NotificationContext);
  const [questionBankInfo, setQuestionBankInfo] = useState({
    name: "",
    language: "",
    tag: "",
    status: "",
  });
  const [qbQuestions, setQbQuestions] = useState([]);
  const [pageState, setPageState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
  });
  const [previewData, setPreviewData] = useState({
    src: "",
    width: 0,
    height: 0,
  });

  const [expandedQuestions, setExpandedQuestions] = useState({});
  const [expandedOptions, setExpandedOptions] = useState({});
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const authData = JSON.parse(localStorage.getItem("p") || "{}");
  const role = authData?.role;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { questionBankDifficultyData } = useSelector(
    (state) => state.questionBanks,
  );

  const levelOptions =
    questionBankDifficultyData?.data?.QuestionDifficultyLevel?.map((item) => ({
      value: item.ID,
      label:
        item.levelName.charAt(0).toUpperCase() +
        item.levelName.slice(1).toLowerCase(),
    })) || [];

  useEffect(() => {
    getQuestionBankDifficultyList(dispatch);
  }, [dispatch]);

  useEffect(() => {
    const fetchDetails = async () => {
      if (!decodedId) return;

      try {
        setPageState({
          isLoading: true,
          error: "",
          noRecords: false,
        });

        const res = await getQuestionBankById(decodedId);

        if (res?.code === 200 && res?.data) {
          const { data } = res;

          setQuestionBankInfo({
            name: data.bankName,
            language:
              LANGUAGE_OPTIONS.find((item) => item.value === data.language)
                ?.label || data.language,
            tag: data.tagName,
            status: data.status,
          });

          const mappedQuestions =
            data.Question?.map((q) => ({
              id: q.ID,
              question: q.question,
              type: q.answerType,
              marks: q.marks,
              level: q.level,
              options:
                q.Answer?.map((ans) => ({
                  text: ans.answer,
                  grade: ans.grade,
                })) || [],
              answer:
                q.Answer?.map((ans, idx) => (ans.grade > 0 ? idx : -1)).filter(
                  (idx) => idx !== -1,
                ) || [],
            })) || [];

          setQbQuestions(mappedQuestions);

          setPageState({
            isLoading: false,
            error: "",
            noRecords: mappedQuestions.length === 0,
          });
        } else {
          setPageState({
            isLoading: false,
            error: res?.message || "Failed to load details.",
            noRecords: false,
          });
        }
      } catch (error) {
        setPageState({
          isLoading: false,
          error: error?.message || "Something went wrong.",
          noRecords: false,
        });
      }
    };

    fetchDetails();
  }, [decodedId, notify]);

  const getPlainText = (html) => {
    if (!html) return "";
    const el = document.createElement("div");
    el.innerHTML = html;
    return el.textContent || "";
  };

  const truncateHtml = (html, limit) => {
    if (!html) return "";
    const container = document.createElement("div");
    container.innerHTML = html;
    let count = 0;
    const walk = (node) => {
      if (count >= limit) {
        node.parentNode?.removeChild(node);
        return;
      }
      if (node.nodeType === Node.TEXT_NODE) {
        const remaining = limit - count;
        if (node.textContent.length > remaining) {
          node.textContent = node.textContent.slice(0, remaining) + "...";
          count = limit;
        } else {
          count += node.textContent.length;
        }
      } else {
        Array.from(node.childNodes).forEach(walk);
      }
    };
    Array.from(container.childNodes).forEach(walk);
    return container.innerHTML;
  };

  const handleImagePreview = (e) => {
    const link = e.target.closest("a");
    if (link) {
      e.preventDefault();
    }

    const img = e.target.closest("img");
    if (!img) return;

    e.preventDefault();
    e.stopPropagation();
    setIsPreviewLoading(true);
    setPreviewData({
      src: img.src,
      width: img.naturalWidth,
      height: img.naturalHeight,
    });
  };

  //close the modal
  const closePreview = () => {
    setPreviewData({
      src: "",
      width: 0,
      height: 0,
    });

    setIsPreviewLoading(false);
  };
  return (
    <>
      <AdminHeader />
      {isPreviewLoading && (
        <div className="logout-overlay">
          <div className="logout-card">
            <i className="fa fa-spinner fa-spin fa-2x text-deep-blue mb-3" />
            <div className="font-weight-bold text-dark">
              Loading preview... Please wait.
            </div>
          </div>
        </div>
      )}

      <Container fluid className="mt--6 course-layout">
        <Row>
          <Col md="12">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div className="d-flex align-items-center text-white ml-1">
                <span className="font-weight-bold small-text">
                  Question Bank Details
                </span>
              </div>
            </div>
            <DetailsHeader
              onBack={() =>
                navigate(
                  role === "SUPER_ADMIN"
                    ? "/super-admin/question-bank"
                    : "/admin/question-bank",
                )
              }
              backTooltip="Back to Question Banks"
              title={
                pageState.isLoading
                  ? "Question Bank Details"
                  : questionBankInfo.name
              }
              status={pageState.isLoading ? "" : questionBankInfo.status}
              onEdit={() =>
                navigate(
                  role === "SUPER_ADMIN"
                    ? `/super-admin/question-bank/edit/${urlEncodedId(decodedId)}`
                    : `/admin/question-bank/edit/${urlEncodedId(decodedId)}`,
                  { state: { mode: "edit" } },
                )
              }
              editLabel="Edit"
              editTooltip="Edit Question Bank"
              editIcon="fa fa-pencil"
              showEdit={
                !pageState.isLoading && questionBankInfo.status === "ACTIVE"
              }
              chips={
                pageState.isLoading
                  ? []
                  : [
                      {
                        id: "qbLanguage",
                        icon: "fas fa-language",
                        name: "Language",
                        value: questionBankInfo.language,
                        tooltip: `Language: ${questionBankInfo.language}`,
                      },
                      {
                        id: "qbTag",
                        icon: "fas fa-tag",
                        name: "Tag",
                        value: questionBankInfo.tag,
                        tooltip: `Tag: ${questionBankInfo.tag}`,
                      },
                    ]
              }
            />
          </Col>
        </Row>

        {pageState.isLoading ? (
          <Row>
            <Col md="12">
              <Card className="mt-3 details-state-card">
                <CardBody className="details-state-body">
                  <div className="loaderContainer">
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        ) : (
          <Row>
            <Col md="12">
              <Card className="mt-3">
                <CardHeader>
                  <h4 className="mb-0 text-default section-title">
                    Total Questions: {qbQuestions.length}
                  </h4>
                </CardHeader>

                <CardBody>
                  {pageState.isLoading ? (
                    <div className="loaderContainer">
                      <i className="fa fa-spinner fa-spin fa-2xl loader" />
                    </div>
                  ) : pageState.error ? (
                    <div className="loaderContainer error-style">
                      <div className="error-icon">
                        <i className="fa-solid fa-circle-exclamation" />
                      </div>
                      <span>{pageState.error}</span>
                    </div>
                  ) : pageState.noRecords ? (
                    <div className="loaderContainer empty-style">
                      <div className="empty-icon">
                        <i className="fa-solid fa-circle-info" />
                      </div>
                      <span>No questions found.</span>
                    </div>
                  ) : (
                    qbQuestions.map((q, index) => (
                      <Card key={q.id} className="section-header mb-4">
                        <CardBody>
                          {/* Header */}
                          <div className="d-flex justify-content-between align-items-start mb-4 question-header">
                            <div className="d-flex flex-grow-1">
                              <div className="question-number-box">
                                Que {index + 1}.
                              </div>

                              <div className="question-content ml-3">
                                {(() => {
                                  const plainText = getPlainText(q.question);
                                  const isExpanded = expandedQuestions[index];
                                  const shouldTrim = plainText.length > PREVIEW_LIMIT;
                                  const displayText = isExpanded || !shouldTrim
                                    ? q.question
                                    : truncateHtml(q.question, PREVIEW_LIMIT);

                                  return (
                                    <>
                                      <div
                                        className="question-html-content small-text"
                                        onClick={handleImagePreview}
                                        dangerouslySetInnerHTML={{
                                          __html: DOMPurify.sanitize(displayText),
                                        }}
                                      />

                                      {shouldTrim && (
                                        <div className="mt-1">
                                          <button
                                            className="lesson-read-more-btn"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setExpandedQuestions((prev) => ({
                                                ...prev,
                                                [index]: !prev[index],
                                              }));
                                            }}
                                          >
                                            {isExpanded ? "Read Less" : "Read More"}
                                          </button>
                                        </div>
                                      )}
                                    </>
                                  );
                                })()}
                              </div>
                            </div>

                            <div className="question-meta">
                              <span className="question-type-badge">
                                {ANSWER_TYPES.find(
                                  (item) => item.value === q.type,
                                )?.label || q.type}
                              </span>

                              <span className="question-mark-badge ml-2">
                                Mark: {String(q.marks).padStart(2, "0")}
                              </span>

                              {q.level && (
                                <span className="question-mark-badge ml-2">
                                  Level:{" "}
                                  {levelOptions.find((item) => item.value === q.level)?.label ||
                                    toTitleCase(q.level || "")}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* SINGLE & MULTIPLE CHOICE */}
                          {(q.type === "SINGLE_CHOICE" ||
                            q.type === "MULTIPLE_CHOICE") && (
                            <Row>
                              {q.options.map((opt, i) => {
                                const isCorrect = q.answer?.includes(i);

                                return (
                                  <Col md="6" key={i} className="mb-3">
                                    <div
                                      className={`question-option-card${
                                        isCorrect
                                          ? " option-correct"
                                          : " option-disabled"
                                      }`}
                                    >
                                      <div className="d-flex align-items-center w-100">
                                        <div
                                          className={`option-icon mr-3${isCorrect ? " text-success" : " text-muted"}`}
                                        >
                                          {q.type === "SINGLE_CHOICE" ? (
                                            <i
                                              className={`fa-${
                                                isCorrect
                                                  ? "solid fa-circle-check text-success"
                                                  : "regular fa-circle text-muted"
                                              }`}
                                            />
                                          ) : (
                                            <i
                                              className={`fa-${
                                                isCorrect
                                                  ? "solid fa-square-check text-success"
                                                  : "regular fa-square text-muted"
                                              }`}
                                            />
                                          )}
                                        </div>

                                        <div className="flex-grow-1">
                                          <div className="d-flex flex-column w-100">
                                            {(() => {
                                              const optText = opt.text || "";
                                              const plainOptText = getPlainText(optText);
                                              const optKey = `${index}-${i}`;
                                              const isOptExpanded = expandedOptions[optKey];
                                              const shouldTrimOpt = plainOptText.length > PREVIEW_LIMIT;
                                              const displayOptText = isOptExpanded || !shouldTrimOpt
                                                ? optText
                                                : truncateHtml(optText, PREVIEW_LIMIT);

                                              return (
                                                <>
                                                  <div className="d-flex align-items-center w-100">
                                                    <div
                                                      className="question-html-content flex-grow-1"
                                                      onClick={handleImagePreview}
                                                      dangerouslySetInnerHTML={{
                                                        __html: DOMPurify.sanitize(displayOptText),
                                                      }}
                                                    />
                                                    {opt.grade !== undefined && opt.grade !== null && (
                                                      <span className="option-grade ml-2">
                                                        ({MULTI_CORRECT_NEGATIVE_OPTIONS.find((g) => g.value === opt.grade)?.label || `${opt.grade}%`})
                                                      </span>
                                                    )}
                                                  </div>
                                                  {shouldTrimOpt && (
                                                    <div className="mt-1">
                                                      <button
                                                        className="lesson-read-more-btn"
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          setExpandedOptions((prev) => ({
                                                            ...prev,
                                                            [optKey]: !prev[optKey],
                                                          }));
                                                        }}
                                                      >
                                                        {isOptExpanded ? "Read Less" : "Read More"}
                                                      </button>
                                                    </div>
                                                  )}
                                                </>
                                              );
                                            })()}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </Col>
                                );
                              })}
                            </Row>
                          )}

                          {/* TRUE FALSE */}
                          {q.type === "TRUE_FALSE" && (
                            <Row>
                              {q.options.map((opt, i) => {
                                const isCorrect = q.answer?.[0] === i;

                                return (
                                  <Col md="6" key={i} className="mb-3">
                                    <div
                                      className={`question-option-card${
                                        isCorrect
                                          ? " option-correct"
                                          : " option-disabled"
                                      }`}
                                    >
                                      <div className="d-flex align-items-center">
                                        <div
                                          className={`option-icon mr-3${isCorrect ? " text-success" : " text-muted"}`}
                                        >
                                          <i
                                            className={`fa-${
                                              isCorrect
                                                ? "solid fa-circle-check text-success"
                                                : "regular fa-circle text-muted"
                                            }`}
                                          />
                                        </div>

                                        <div className="flex-grow-1">
                                          <div className="d-flex flex-column w-100">
                                            {(() => {
                                              const optText = opt.text || "";
                                              const plainOptText = getPlainText(optText);
                                              const optKey = `${index}-${i}`;
                                              const isOptExpanded = expandedOptions[optKey];
                                              const shouldTrimOpt = plainOptText.length > PREVIEW_LIMIT;
                                              const displayOptText = isOptExpanded || !shouldTrimOpt
                                                ? optText
                                                : truncateHtml(optText, PREVIEW_LIMIT);

                                              return (
                                                <>
                                                  <div className="d-flex align-items-center w-100">
                                                    <div
                                                      className="question-html-content flex-grow-1"
                                                      onClick={handleImagePreview}
                                                      dangerouslySetInnerHTML={{
                                                        __html: DOMPurify.sanitize(displayOptText),
                                                      }}
                                                    />
                                                  </div>
                                                  {shouldTrimOpt && (
                                                    <div className="mt-1">
                                                      <button
                                                        className="lesson-read-more-btn"
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          setExpandedOptions((prev) => ({
                                                            ...prev,
                                                            [optKey]: !prev[optKey],
                                                          }));
                                                        }}
                                                      >
                                                        {isOptExpanded ? "Read Less" : "Read More"}
                                                      </button>
                                                    </div>
                                                  )}
                                                </>
                                              );
                                            })()}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </Col>
                                );
                              })}
                            </Row>
                          )}

                          {/* IMAGE CHOICE */}
                          {q.type === "IMAGE_CHOICE" && (
                            <Row className="mt-3">
                              {q.options.map((opt, i) => {
                                const isCorrect = q.answer?.includes(i);

                                return (
                                  <Col
                                    lg="3"
                                    md="4"
                                    sm="6"
                                    xs="12"
                                    key={i}
                                    className="mb-3"
                                  >
                                    <div
                                      className={`image-option-card${
                                        isCorrect
                                          ? " option-correct"
                                          : " option-disabled"
                                      }`}
                                    >
                                      {/* Show image only if available */}
                                      {opt.image && (
                                        <div className="image-wrapper mb-2">
                                          <img
                                            src={opt.image}
                                            alt={`Option ${i + 1}`}
                                            className="img-fluid rounded"
                                          />
                                        </div>
                                      )}

                                      <div className="d-flex align-items-center">
                                        <div
                                          className={`option-icon mr-2${isCorrect ? " text-success" : " text-muted"}`}
                                        >
                                          <i
                                            className={`fa-${
                                              isCorrect
                                                ? "solid fa-square-check text-success"
                                                : "regular fa-square text-muted"
                                            }`}
                                          />
                                        </div>

                                        <div className="flex-grow-1">
                                          <div className="d-flex flex-column w-100">
                                            {(() => {
                                              const optText = opt.text || "";
                                              const plainOptText = getPlainText(optText);
                                              const optKey = `${index}-${i}`;
                                              const isOptExpanded = expandedOptions[optKey];
                                              const shouldTrimOpt = plainOptText.length > PREVIEW_LIMIT;
                                              const displayOptText = isOptExpanded || !shouldTrimOpt
                                                ? optText
                                                : truncateHtml(optText, PREVIEW_LIMIT);

                                              return (
                                                <>
                                                  <div className="d-flex align-items-center w-100">
                                                    <div
                                                      className="question-html-content flex-grow-1"
                                                      onClick={handleImagePreview}
                                                      dangerouslySetInnerHTML={{
                                                        __html: DOMPurify.sanitize(displayOptText),
                                                      }}
                                                    />
                                                  </div>
                                                  {shouldTrimOpt && (
                                                    <div className="mt-1">
                                                      <button
                                                        className="lesson-read-more-btn"
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          setExpandedOptions((prev) => ({
                                                            ...prev,
                                                            [optKey]: !prev[optKey],
                                                          }));
                                                        }}
                                                      >
                                                        {isOptExpanded ? "Read Less" : "Read More"}
                                                      </button>
                                                    </div>
                                                  )}
                                                </>
                                              );
                                            })()}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </Col>
                                );
                              })}
                            </Row>
                          )}
                        </CardBody>
                      </Card>
                    ))
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
      </Container>
      <Modal
        isOpen={!!previewData.src}
        toggle={closePreview}
        size="xl"
        centered
      >
        <ModalHeader toggle={closePreview}>Image Preview</ModalHeader>

        <ModalBody className="text-center">
          <img
            src={previewData.src}
            alt="Preview"
            className="preview-image"
            style={{
              visibility: isPreviewLoading ? "hidden" : "visible",
            }}
            onLoad={() => setIsPreviewLoading(false)}
            onError={() => setIsPreviewLoading(false)}
          />
        </ModalBody>
      </Modal>
    </>
  );
}

export default QuestionBankDetails;
